import { Keyboard } from '@capacitor/keyboard';

import { isPlatform } from '@ionic/vue';


/*
  Defensive reset for a layout that stays stuck at a reduced height after the
  keyboard closes.

  capacitor.config.ts runs the keyboard in `resize: 'Body'` mode: while the
  keyboard is open the native plugin sets an inline `document.body.style.height`,
  and on close it clears that height again — but via a *delayed* `_updateFrame`
  scheduled from `onKeyboardWillHide` (see @capacitor/keyboard
  ios/Plugin/Keyboard.m, `performSelector:withObject:afterDelay:`).

  The whole app hangs off a pure `height: 100%` chain (html -> body -> #app, with
  `overflow: hidden`) and the tab bar is `position: absolute; bottom: 0` inside it
  (ZeitTabMenu.vue). So if that delayed clear is dropped — e.g. when the keyboard
  hides at the same moment the absence-application form's card / route transition
  tears the view down — the body stays shrunk and the tab bar rides up into the
  middle of the screen, in every tab, until the app is restarted (reported via
  TestFlight on iOS 26).

  `keyboardDidHide` fires from a separate native notification that is not part of
  that delayed-resize race, so we clear the inline height here as a safety net.
  This mirrors the plugin's own success-case reset (it sets the height to `null`),
  so it is a harmless no-op whenever the plugin already restored the height.
*/
export async function initKeyboardLayoutFix(): Promise<void> {
  if (!isPlatform('capacitor')) return;

  await Keyboard.addListener('keyboardDidHide', () => {
    document.body.style.removeProperty('height');
  });
}
